'use client';

import React, { useState, useEffect, useRef } from "react";

interface IAutoIncrementCounterProps {
    className?: string;
    target: number;
    duration: number;
}

const AutoIncrementCounter = React.memo<IAutoIncrementCounterProps>(({ className, target, duration }) => {
    const [count, setCount] = useState<number>(0);
    const rafRef = useRef<number | null>(null);
    const isMountedRef = useRef<boolean>(true);

    useEffect(() => {
        isMountedRef.current = true;
        let start: number | null = null;

        const step = (timestamp: number) => {
            if (!isMountedRef.current) return;
            
            if (start === null) start = timestamp;

            const progress = timestamp - start;
            const progressRatio = Math.min(progress / duration, 1);
            const newCount = Math.floor(progressRatio * target);
            
            setCount(newCount);

            if (progress < duration) {
                rafRef.current = requestAnimationFrame(step);
            } else {
                setCount(target);
                rafRef.current = null;
            }
        };

        rafRef.current = requestAnimationFrame(step);

        return () => {
            isMountedRef.current = false;
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
        };
    }, [target, duration]);

    return (
        <span className={className}>
            {count}
        </span>
    );
});

AutoIncrementCounter.displayName = 'AutoIncrementCounter';

export default AutoIncrementCounter;

